import React, { useEffect } from 'react';
import { Layout, useSidebarState, Sidebar, useStore } from 'react-admin';
import { useMediaQuery, type Theme } from '@mui/material';
import CustomAppBar from './CustomAppBar';
import CustomMenu from './CustomMenu';
import type { Partner } from '@src/types';

/**
 * The nav pane stays fully expanded on desktop — no hover pop-out and no
 * open/close width animation. On phones react-admin renders the sidebar as a
 * temporary drawer, which keeps its hamburger toggle (see CustomAppBar).
 */
const PinnedSidebar = ({ children }: { children: React.ReactElement }) => (
  <Sidebar
    sx={theme => ({
      // Desktop only: the pane never opens or closes, so the width animation is
      // dead weight. The phone drawer still slides in, so leave it alone.
      [theme.breakpoints.up('sm')]: {
        '& .RaSidebar-fixed, & .MuiDrawer-paper': { transition: 'none' },
      },
    })}
  >
    {children}
  </Sidebar>
);

export const CustomLayout = ({ children }: { children: React.ReactNode }) => {
  const [, setSidebarOpen] = useSidebarState();
  const isXSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [currentPartner, setCurrentPartner] = useStore<Partner | null>('currentPartner', null);

  // Pin the sidebar open on desktop, overriding any collapsed state react-admin
  // persisted from before it was pinned. Writing `false` below `sm` matters:
  // `sidebar.open` is persisted, and on phones react-admin renders the sidebar
  // as a temporary drawer driven by that same flag — leaving it `true` pops the
  // nav open as a modal overlay the moment the viewport narrows.
  useEffect(() => {
    setSidebarOpen(!isXSmall);
  }, [isXSmall, setSidebarOpen]);

  // Initialize currentPartner from sessionStorage if not already set in store
  // This ensures single-partner users have their partner loaded on all pages
  useEffect(() => {
    if (!currentPartner) {
      try {
        const selectedPartner = sessionStorage.getItem('selectedPartner');
        if (selectedPartner) {
          const parsedPartner = JSON.parse(selectedPartner);
          setCurrentPartner(parsedPartner);
        }
      } catch (error) {
        console.error('Failed to initialize partner from sessionStorage:', error);
      }
    }
  }, [currentPartner, setCurrentPartner]);

  return (
    <Layout appBar={CustomAppBar} menu={CustomMenu} sidebar={PinnedSidebar}>
      {children}
    </Layout>
  );
};

export default CustomLayout;
